import React, { useMemo } from 'react';
import { Text } from '@ui';
import { translate } from '@utils/i18n';
import { useQuery } from '@tanstack/react-query';
import { AttachmentsSurface } from '@features/Attachments/AttachmentsSurface';
import { attachmentsKeys } from '@features/Attachments/hooks/queryKeys';
import { createBulkDestroyDocuments, createDestroyDocument, createFetchDocuments } from '../services/subcontractorDocumentsApi';
import { createDirectUploadTransport } from '../services/directUploadTransport';
import { toSubcontractorDocumentRow } from '../utils/toSubcontractorDocumentRow';
import { buildDocumentColumns, SUBCONTRACTOR_SORTABLE_COLUMNS } from '../utils/documentColumns';
import { buildAddAttachmentsColumns, buildAddAttachmentsLabels, SUBCONTRACTOR_DEFAULT_UPLOAD_ATTRIBUTES, toSubcontractorQueueFields } from '../utils/addAttachmentsConfig';
import { COMMONS_NS, MASS_ACTIONS_NS, PROGRESS_TOAST_NS, SUBCONTRACTOR_DOCUMENTS_ENTITY_KEY, SUBCONTRACTOR_DOCUMENTS_NS, UPLOAD_NS } from '../constants';
/**
 * One document type's group: its own header, its own Add action, its own table.
 *
 * Each group is a full `AttachmentsSurface` keyed on the document type, so React Query caches,
 * selection and upload queues are per group — which is what makes a drawer bound to one type
 * (plan decision 5) work without the surface knowing anything about grouping.
 *
 * `ownerId` is the DOCUMENT TYPE id, because that is what the registry declares as this type's
 * owner and what the signed upload token is minted against. The parent object travels separately
 * through the service factories: a document needs both, and only the type is an owner in the
 * direct-upload layer's sense.
 */
export function SubcontractorDocumentGroup({ group, parent }) {
    const uploadLabels = useMemo(() => buildAddAttachmentsLabels(group.maxUploadSizeBytes, group.acceptContentTypes), [group.maxUploadSizeBytes, group.acceptContentTypes]);
    const accept = useMemo(() => { var _a; return (_a = group.acceptContentTypes) === null || _a === void 0 ? void 0 : _a.join(','); }, [group.acceptContentTypes]);
    const config = useMemo(() => ({
        // Namespaced per document type so two groups on the same page never share a cache entry.
        entityKey: `${SUBCONTRACTOR_DOCUMENTS_ENTITY_KEY}:${group.documentTypeId}`,
        namespace: SUBCONTRACTOR_DOCUMENTS_NS,
        progressToastNamespace: PROGRESS_TOAST_NS,
        className: 'subcontractor-documents-table',
        data: {
            fetchAll: createFetchDocuments(parent),
            toRow: toSubcontractorDocumentRow,
            destroyOne: createDestroyDocument(parent),
            destroyMany: createBulkDestroyDocuments(parent)
        },
        labels: {
            deleteSuccessToast: (count) => translate('toasts.delete_success', { namespace: MASS_ACTIONS_NS, count }),
            deleteErrorToast: translate('toasts.delete_error', { namespace: MASS_ACTIONS_NS }),
            deleteConfirmBulkTitle: (count) => translate('delete_confirm.bulk_title', { namespace: MASS_ACTIONS_NS, count }),
            deleteConfirmSingleTitle: (fileName) => translate('delete_confirm.single_title', { namespace: MASS_ACTIONS_NS, fileName }),
            deleteConfirmDescription: translate('delete_confirm.description', { namespace: MASS_ACTIONS_NS }),
            deleteConfirmLabel: translate('delete_confirm.confirm_label', { namespace: MASS_ACTIONS_NS }),
            deleteCancelLabel: translate('cancel', { namespace: COMMONS_NS }),
            rowDeleteAriaLabel: (fileName) => translate('row_delete_aria_label', { namespace: MASS_ACTIONS_NS, fileName })
        },
        // Static, not an inline editor: `subcontractor_documents` has no `name` column, so there is
        // nothing to PATCH and the legacy screen never offered a rename either.
        renderNameCell: (row) => React.createElement(Text, { variant: "body" }, row.name),
        entityColumns: ({ t: surfaceT }) => buildDocumentColumns({ translateFn: surfaceT }),
        entitySortableColumns: SUBCONTRACTOR_SORTABLE_COLUMNS,
        searchableText: (row) => [row.name, row.uploaderName],
        upload: {
            transport: (documentTypeId) => createDirectUploadTransport(parent, documentTypeId),
            defaultAttributes: SUBCONTRACTOR_DEFAULT_UPLOAD_ATTRIBUTES,
            toFields: toSubcontractorQueueFields,
            columns: buildAddAttachmentsColumns,
            labels: uploadLabels,
            accept,
            successToast: (count) => translate('toasts.success', { namespace: UPLOAD_NS, count })
        }
    }), [group.documentTypeId, parent, uploadLabels, accept]);
    // Re-derived from the rows in the cache, not taken from the page prop.
    //
    // `group.canAdd` is the server's answer at page LOAD and nothing refreshed it: deleting the only
    // document in a single-slot type kept Add hidden until reload, and after an upload Add stayed
    // visible and the next upload got a 422 `slot_filled` (release review of PR #23938). The server
    // guard is the control and stays -- this only stops the UI offering an action already refused.
    //
    // Read from the same key `AttachmentsSurface` fetches into rather than lifting the query out of
    // it, so there is still one request and one owner of the data. `group.canAdd` remains the value
    // until that cache is populated, which is what the first paint shows.
    //
    // `useQuery` with `enabled: false`, NOT `getQueryData`. The surface is this component's CHILD, so
    // its re-render does not re-render this one -- a plain cache read would be taken once and never
    // again, which looks identical to the frozen prop it replaces. This subscribes to the entry
    // without owning it: no `queryFn`, so it never fetches and the surface stays the only requester.
    const { data: cachedRows } = useQuery({
        queryKey: attachmentsKeys(`${SUBCONTRACTOR_DOCUMENTS_ENTITY_KEY}:${group.documentTypeId}`)
            .list(group.documentTypeId),
        enabled: false
    });
    const canAdd = cachedRows ? group.canCreate && (group.hasMultiple || cachedRows.length === 0) : group.canAdd;
    return (React.createElement(AttachmentsSurface, { config: config, ownerId: group.documentTypeId, maxUploadSizeBytes: group.maxUploadSizeBytes, title: groupTitle(group), canAdd: canAdd }));
}
/**
 * The type's configured title, with the required marker the legacy table showed as a `*` beside
 * it. Required-ness is a property of the document type, so it belongs in the heading rather than
 * repeated on every row.
 */
function groupTitle(group) {
    return group.isRequired ? `${group.title} *` : group.title;
}
